import { Component, computed, inject, signal } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCheck, faDatabase, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FieldMappingComponent, InstructionsComponent } from '@r1-compass-library/components';
import {
    DataMartField,
    DefinedField,
    DivisionMappingRowType,
    DivisionMappingSettings,
    predefinedFields,
    SalesforceFieldMetadata,
    SalesforceListFieldResponse,
} from '@r1-compass-library/models';
import { Uuid } from '@r1-compass-library/utilities/uuid';
import { delay, take, tap } from 'rxjs';
import { DivisionDataMappingFormService } from '../division-data-mapping-form.service';
import { DivisionDataMappingCompatibleTypesService } from '../division-data-mapping-types.service';
import { DataMappingFormValue, DivisionDataMappingFieldMappingFormValue, FieldMappingForm } from '../form.model';

type ValueReservation = { value: SalesforceFieldMetadata; reservedBy: number };

@Component({
    selector: 'r1-compass-app-field-mapping-mapper',
    imports: [
        FieldMappingComponent,
        FontAwesomeModule,
        InstructionsComponent,
        MatButtonModule,
        MatButtonToggleModule,
        MatSelectModule,
        ReactiveFormsModule,
        MatButtonToggleModule,
        MatTooltip,
    ],
    templateUrl: './field-mapping-mapper.component.html',
    styles: `
        .toggle-div {
            display: flex;
            justify-content: center;
            --mat-button-toggle-selected-state-background-color: var(--r1-brilliant-blue);
            --mat-button-toggle-selected-state-text-color: var(--r1-white);
        }
    `,
})
export class FieldMappingMapperComponent {
    readonly #library = inject(FaIconLibrary);
    readonly #divisionDataMappingComatibleTypesService = inject(DivisionDataMappingCompatibleTypesService);
    protected readonly formService = inject(DivisionDataMappingFormService);

    protected formArray: FormArray<FormGroup<FieldMappingForm>> =
        this.formService.divisionDataMappingForm.controls.fieldMapping;
    private formState = this.formService.dataMappingFormState;

    private fieldMappingsValue = computed<DivisionDataMappingFieldMappingFormValue[]>(
        () => this.formState.value().fieldMapping
    );

    protected Uuid = Uuid;

    private selectedDivisionConfigMapping = computed<DivisionMappingSettings | undefined>(
        () => this.formState.selectedDivisionConfig()?.config?.mapping
    );

    private playValuesWithEnabledStatus = computed<ValueReservation[]>(() => {
        const formStateValue = this.formState.value();
        const formStatePlayFieldsOptions = this.formState.playFieldsOptions();

        const { usedMappings, unusedValues, fields } = this.mapUsedMappingsAndUnusedValues(
            formStateValue,
            formStatePlayFieldsOptions,
            'play'
        );

        return this.enabledSalesforceFieldsGenerator(usedMappings, unusedValues, fields);
    });

    private recommendationValuesWithEnabledStatus = computed<ValueReservation[]>(() => {
        const formStateValue = this.formState.value();
        const formStateRecommendationFieldsOptions = this.formState.recommendationFieldsOptions();

        const { usedMappings, unusedValues, fields } = this.mapUsedMappingsAndUnusedValues(
            formStateValue,
            formStateRecommendationFieldsOptions,
            'rec'
        );

        return this.enabledSalesforceFieldsGenerator(usedMappings, unusedValues, fields);
    });

    private additionalFields = computed<DefinedField[]>(() =>
        this.filterAdditionalFields(this.selectedDivisionConfigMapping()?.additionalFields ?? [])
    );

    protected readonly predefinedFields = predefinedFields.sort(function (a, b) {
        return a.name.localeCompare(b.name);
    });

    protected readonly requiredPredefinedFieldsLength = signal(predefinedFields.filter(field => field.required).length);

    constructor() {
        const library = this.#library;

        library.addIcons(faDatabase, faPlus, faCheck);
    }

    protected filteredDataMartValues = computed<DataMartField[][]>(() => {
        return this.filterDatamartValues(
            this.formState.dataMartFields(),
            this.formState.playFieldsOptions(),
            this.fieldMappingsValue()
        );
    });

    protected filteredDefinedFieldValues = computed<DefinedField[][]>(() => {
        return this.filterDefinedFields(
            this.additionalFields(),
            this.formState.playFieldsOptions(),
            this.fieldMappingsValue()
        );
    });

    /**
     * Gets filtered Play field values based on the selected DataMart field type or Salesforce Recommendation field type for a specific row
     */
    protected filteredPlayValues = computed<ValueReservation[][]>(() => {
        return this.filterPlayValues(
            this.formState.dataMartFields(),
            this.formState.playFieldsOptions(),
            this.playValuesWithEnabledStatus(),
            this.additionalFields(),
            this.fieldMappingsValue()
        );
    });

    /**
     * Gets filtered Recommendation field values based on the selected DataMart field type or Salesforce Play field type for a specific row
     */
    protected filteredRecommendationValues = computed<ValueReservation[][]>(() => {
        return this.filterRecommendationValues(
            this.formState.dataMartFields(),
            this.formState.playFieldsOptions(),
            this.additionalFields(),
            this.recommendationValuesWithEnabledStatus(),
            this.fieldMappingsValue()
        );
    });

    protected forceValidation(index: number) {
        switch (this.formArray.controls.at(index)?.controls.mappingType.value) {
            case 'play':
                this.formArray.at(index).controls.targetRecommendationField.reset();
                break;
            case 'rec':
                this.formArray.at(index).controls.targetPlayField.reset();
                break;
            case 'both':
                this.formArray.at(index).controls.targetPlayField.reset();
                this.formArray.at(index).controls.targetRecommendationField.reset();
                break;
        }
    }

    // When the source field changes, revalidate both targets so any previous mismatch errors are cleared/added
    protected onSourceFieldChanged(index: number) {
        const row = this.formArray.at(index);
        if (!row) return;

        // Defer sibling validations to ensure the new value is fully applied
        row.valueChanges
            .pipe(
                delay(1), // ms
                tap(() => {
                    row.controls.targetPlayField.updateValueAndValidity({ onlySelf: true, emitEvent: true });
                    row.controls.targetRecommendationField.updateValueAndValidity({ onlySelf: true, emitEvent: true });
                }),
                take(1)
            )
            .subscribe();

        // Mark as dirty so validators that check .dirty can propagate updates
        row.controls.sourceField.markAllAsDirty();
        // First validate the changed control itself
        row.controls.sourceField.updateValueAndValidity({ onlySelf: true, emitEvent: true });
    }

    // When the Play target changes, revalidate the source and the Recommendation target
    protected onTargetPlayFieldChanged(index: number) {
        const row = this.formArray.at(index);
        if (!row) return;

        // Defer sibling validations to ensure the new value is fully applied
        row.valueChanges
            .pipe(
                delay(1), // ms
                tap(() => {
                    row.controls.sourceField.updateValueAndValidity({ onlySelf: true, emitEvent: true });
                    row.controls.targetRecommendationField.updateValueAndValidity({ onlySelf: true, emitEvent: true });
                }),
                take(1)
            )
            .subscribe();

        // Mark as dirty so validators that check .dirty can propagate updates
        row.controls.targetPlayField.markAllAsDirty();
        // Validate changed control first
        row.controls.targetPlayField.updateValueAndValidity({ onlySelf: true, emitEvent: true });
    }

    // When the Recommendation target changes, revalidate the source and the Play target
    protected onTargetRecommendationFieldChanged(index: number) {
        const row = this.formArray.at(index);
        if (!row) return;

        // Defer sibling validations to ensure the new value is fully applied
        row.valueChanges
            .pipe(
                delay(1), // ms
                tap(() => {
                    row.controls.sourceField.updateValueAndValidity({ onlySelf: true, emitEvent: true });
                    row.controls.targetPlayField.updateValueAndValidity({ onlySelf: true, emitEvent: true });
                }),
                take(1)
            )
            .subscribe();

        // Mark as dirty so validators that check .dirty can propagate updates
        row.controls.targetRecommendationField.markAllAsDirty();
        // Validate changed control first
        row.controls.targetRecommendationField.updateValueAndValidity({ onlySelf: true, emitEvent: true });
    }

    protected deleteMapperRow(index: number) {
        this.formArray.removeAt(index);
        this.formArray.markAllAsDirty();
        this.formService.divisionDataMappingForm.markAllAsDirty();
    }

    protected addMapperRow() {
        const rowId = Uuid.generate();

        this.formService.addMapperRow(rowId, this.formArray);
        this.formArray.markAllAsDirty();
        this.formService.divisionDataMappingForm.markAllAsDirty();
    }

    protected setFieldType(type: DivisionMappingRowType, index: number) {
        const fieldMappingFormGroup = this.formArray.controls.at(index);
        fieldMappingFormGroup?.controls.sourceField.reset();

        switch (type) {
            case 'predefinedField':
                fieldMappingFormGroup?.controls.fieldType.setValue('predefinedField');
                fieldMappingFormGroup?.controls.id?.setValue(''); // this will be overwritten on pick in the template anyways
                break;
            case 'definedField':
                fieldMappingFormGroup?.controls.fieldType.setValue('definedField');
                fieldMappingFormGroup?.controls.id?.setValue(Uuid.generate());
                break;
            case 'dataMart':
                fieldMappingFormGroup?.controls.fieldType.setValue('dataMart');
                fieldMappingFormGroup?.controls.id?.setValue(Uuid.generate());
                break;
        }
    }

    private filterDefinedFields(
        additionalFields: DefinedField[],
        playFieldsOptionsFormState: SalesforceListFieldResponse | undefined,
        fieldMappingsValue: DivisionDataMappingFieldMappingFormValue[]
    ) {
        return fieldMappingsValue.map(row => {
            const definedField = row.sourceField;
            const playField = row.targetPlayField;
            const recField = row.targetRecommendationField;

            if (!playField && !recField) {
                return additionalFields.map(field => ({ ...field, valid: true }));
            }

            const fieldName = playField || recField;
            const salesforceField = playFieldsOptionsFormState?.fields.find(f => f.name === fieldName);
            if (!salesforceField) {
                return additionalFields.map(field => ({ ...field, valid: true }));
            }

            const compatibleTypes = this.#divisionDataMappingComatibleTypesService.getCompatibleDefinedFieldTypes(
                salesforceField.type
            );
            if (compatibleTypes.length === 0) {
                return additionalFields.map(field => ({ ...field, valid: true }));
            }

            const definedFields: (DefinedField & { valid: boolean })[] = additionalFields
                .filter(field => compatibleTypes.includes(field.type.toLowerCase()))
                .map(field => ({ ...field, valid: true }));
            if (!definedFields.find(field => field.id === definedField)) {
                const existingField = additionalFields.find(field => field.id === definedField);
                if (existingField) {
                    definedFields.push({ ...existingField, valid: false });
                }
            }

            return definedFields;
        });
    }

    private filterRecommendationValues(
        datamartFieldsFormState: DataMartField[],
        playFieldsOptionsFormState: SalesforceListFieldResponse | undefined,
        definedFieldsWithTypes: DefinedField[],
        recommendationValuesWithEnabledStatus: ValueReservation[],
        fieldMappingValue: DivisionDataMappingFieldMappingFormValue[]
    ) {
        return fieldMappingValue.map(row => {
            if (!row) {
                return recommendationValuesWithEnabledStatus;
            }

            const sourceField = row.sourceField;
            const targetPlayField = row.targetPlayField;
            const targetRecommendationField = row.targetRecommendationField;
            const fieldType = row.fieldType;

            // Handle defined fields
            if (fieldType === 'definedField') {
                if (!sourceField) {
                    return recommendationValuesWithEnabledStatus;
                }

                // Find the defined field using the signal - look up by ID since that's what's stored in the form control
                const definedField = definedFieldsWithTypes.find(f => f.id === sourceField);
                if (!definedField) {
                    return recommendationValuesWithEnabledStatus;
                }

                // For non-picklist defined fields, get compatible types (same pattern as DataMart)
                let compatibleTypes: string[] = [];
                if (definedField.type === 'varchar' || definedField.type === 'picklist') {
                    compatibleTypes =
                        this.#divisionDataMappingComatibleTypesService.getCompatibleSalesforceTypes('varchar');
                } else if (definedField.type === 'date') {
                    compatibleTypes =
                        this.#divisionDataMappingComatibleTypesService.getCompatibleSalesforceTypes('date');
                } else if (definedField.type === 'boolean') {
                    compatibleTypes =
                        this.#divisionDataMappingComatibleTypesService.getCompatibleSalesforceTypes('boolean');
                }

                if (compatibleTypes.length === 0) {
                    return recommendationValuesWithEnabledStatus;
                }

                // Filter by compatible types (same pattern as DataMart)
                const recommendationFields = recommendationValuesWithEnabledStatus.filter(reservation =>
                    compatibleTypes.includes(reservation.value.type.toLowerCase())
                );
                // Include currently selected field even if incompatible (same pattern as DataMart)
                if (!recommendationFields.find(field => field.value.name === targetRecommendationField)) {
                    const existingField = recommendationValuesWithEnabledStatus.find(
                        field => field.value.name === targetRecommendationField
                    );
                    if (existingField) {
                        recommendationFields.push(existingField);
                    }
                }

                return recommendationFields;
            }

            // Handle dataMart fields
            if (fieldType !== 'dataMart') {
                return recommendationValuesWithEnabledStatus;
            }

            if (!sourceField && !targetPlayField) {
                return recommendationValuesWithEnabledStatus;
            }

            //if we've already set another Salesforce field as the target we can use those types
            if (targetPlayField) {
                const salesforceField = playFieldsOptionsFormState?.fields.find(f => f.name === targetPlayField);
                if (salesforceField) {
                    const salesforceTypes = this.#divisionDataMappingComatibleTypesService.getSalesforceTypes(
                        salesforceField.type
                    );
                    const recommendationFields = recommendationValuesWithEnabledStatus.filter(reservation =>
                        salesforceTypes.includes(reservation.value.type.toLowerCase())
                    );
                    if (!recommendationFields.find(field => field.value.name === targetRecommendationField)) {
                        const existingField = recommendationValuesWithEnabledStatus.find(
                            field => field.value.name === targetRecommendationField
                        );
                        if (existingField) {
                            recommendationFields.push(existingField);
                        }
                    }
                    return recommendationFields;
                }
            }

            const dataMartField = datamartFieldsFormState.find(f => f.name === sourceField);
            if (!dataMartField) {
                return recommendationValuesWithEnabledStatus;
            }

            const compatibleTypes = this.#divisionDataMappingComatibleTypesService.getCompatibleSalesforceTypes(
                dataMartField.dataType
            );
            if (compatibleTypes.length === 0) {
                return recommendationValuesWithEnabledStatus;
            }

            const recommendationFields = recommendationValuesWithEnabledStatus.filter(reservation =>
                compatibleTypes.includes(reservation.value.type.toLowerCase())
            );
            if (!recommendationFields.find(field => field.value.name === targetRecommendationField)) {
                const existingField = recommendationValuesWithEnabledStatus.find(
                    field => field.value.name === targetRecommendationField
                );
                if (existingField) {
                    recommendationFields.push(existingField);
                }
            }

            return recommendationFields;
        });
    }

    private filterDatamartValues(
        datamartFieldsFormState: DataMartField[],
        playFieldsOptionsFormState: SalesforceListFieldResponse | undefined,
        fieldMappingsValue: DivisionDataMappingFieldMappingFormValue[]
    ) {
        return fieldMappingsValue.map(row => {
            const dataMartField = row.sourceField;
            const playField = row.targetPlayField;
            const recField = row.targetRecommendationField;

            if (!playField && !recField) {
                return datamartFieldsFormState;
            }

            const fieldName = playField || recField;
            const salesforceField = playFieldsOptionsFormState?.fields.find(f => f.name === fieldName);
            if (!salesforceField) {
                return datamartFieldsFormState;
            }

            const compatibleTypes = this.#divisionDataMappingComatibleTypesService.getCompatibleDataMartTypes(
                salesforceField.type
            );
            if (compatibleTypes.length === 0) {
                return datamartFieldsFormState;
            }

            const dataMartFields = datamartFieldsFormState.filter(field =>
                compatibleTypes.includes(field.dataType.toLowerCase())
            );
            if (!dataMartFields.find(field => field.name === dataMartField)) {
                const existingField = datamartFieldsFormState.find(field => field.name === dataMartField);
                if (existingField) {
                    dataMartFields.push(existingField);
                }
            }

            return dataMartFields;
        });
    }

    private filterPlayValues(
        datamartFieldsFormState: DataMartField[],
        playFieldsOptionsFormState: SalesforceListFieldResponse | undefined,
        playValuesWithEnabledStatus: ValueReservation[],
        definedFieldsWithTypes: DefinedField[],
        fieldMappingsValue: DivisionDataMappingFieldMappingFormValue[]
    ) {
        return fieldMappingsValue.map(row => {
            if (!row) {
                return playValuesWithEnabledStatus;
            }

            const sourceField = row.sourceField;
            const targetPlayField = row.targetPlayField;
            const targetRecommendationField = row.targetRecommendationField;
            const fieldType = row.fieldType;

            // Handle defined fields
            if (fieldType === 'definedField') {
                if (!sourceField) {
                    return playValuesWithEnabledStatus;
                }

                // Find the defined field using the signal - look up by ID since that's what's stored in the form control
                const definedField = definedFieldsWithTypes.find(f => f.id === sourceField);

                if (!definedField) {
                    return playValuesWithEnabledStatus;
                }

                // For non-picklist defined fields, get compatible types (same pattern as DataMart)
                let compatibleTypes: string[] = [];
                if (definedField.type === 'varchar' || definedField.type === 'picklist') {
                    compatibleTypes =
                        this.#divisionDataMappingComatibleTypesService.getCompatibleSalesforceTypes('varchar');
                } else if (definedField.type === 'date') {
                    compatibleTypes =
                        this.#divisionDataMappingComatibleTypesService.getCompatibleSalesforceTypes('date');
                } else if (definedField.type === 'boolean') {
                    compatibleTypes =
                        this.#divisionDataMappingComatibleTypesService.getCompatibleSalesforceTypes('boolean');
                }

                if (compatibleTypes.length === 0) {
                    return playValuesWithEnabledStatus;
                }

                // Filter by compatible types (same pattern as DataMart)
                const playFields = playValuesWithEnabledStatus.filter(reservation =>
                    compatibleTypes.includes(reservation.value.type.toLowerCase())
                );

                // Include currently selected field even if incompatible (same pattern as DataMart)
                if (!playFields.find(field => field.value.name === targetPlayField)) {
                    const existingField = playValuesWithEnabledStatus.find(
                        field => field.value.name === targetPlayField
                    );
                    if (existingField) {
                        playFields.push(existingField);
                    }
                }

                return playFields;
            }

            // Handle dataMart fields
            if (fieldType !== 'dataMart') {
                return playValuesWithEnabledStatus;
            }

            if (!sourceField && !targetRecommendationField) {
                return playValuesWithEnabledStatus;
            }

            //if we've already set another Salesforce field as the target we can use those types
            if (targetRecommendationField) {
                const salesforceField = playFieldsOptionsFormState?.fields.find(
                    f => f.name === targetRecommendationField
                );
                if (salesforceField) {
                    const salesforceTypes = this.#divisionDataMappingComatibleTypesService.getSalesforceTypes(
                        salesforceField.type
                    );
                    const playFields = playValuesWithEnabledStatus.filter(reservation =>
                        salesforceTypes.includes(reservation.value.type.toLowerCase())
                    );
                    if (!playFields.find(field => field.value.name === targetPlayField)) {
                        const existingField = playValuesWithEnabledStatus.find(
                            field => field.value.name === targetPlayField
                        );
                        if (existingField) {
                            playFields.push(existingField);
                        }
                    }
                    return playFields;
                }
            }

            const dataMartField = datamartFieldsFormState.find(f => f.name === sourceField);
            if (!dataMartField) {
                return playValuesWithEnabledStatus;
            }

            const compatibleTypes = this.#divisionDataMappingComatibleTypesService.getCompatibleSalesforceTypes(
                dataMartField.dataType
            );
            if (compatibleTypes.length === 0) {
                return playValuesWithEnabledStatus;
            }

            const playFields = playValuesWithEnabledStatus.filter(reservation =>
                compatibleTypes.includes(reservation.value.type.toLowerCase())
            );
            if (!playFields.find(field => field.value.name === targetPlayField)) {
                const existingField = playValuesWithEnabledStatus.find(field => field.value.name === targetPlayField);
                if (existingField) {
                    playFields.push(existingField);
                }
            }

            return playFields;
        });
    }

    private filterAdditionalFields(additionalFields: DefinedField[]) {
        return additionalFields.sort(function (a, b) {
            return a.name.localeCompare(b.name);
        });
    }

    private mapUsedMappingsAndUnusedValues(
        formStateValue: DataMappingFormValue,
        fieldOptions: SalesforceListFieldResponse | undefined,
        type: 'play' | 'rec'
    ) {
        const fields = fieldOptions?.fields ?? [];
        const usedMappings = formStateValue.fieldMapping.map((field, index) => ({
            takenValue: type === 'play' ? field.targetPlayField : field.targetRecommendationField,
            takenIndex: index,
        }));
        const unusedValues = fields.filter(
            field => !usedMappings.map(usedValue => usedValue.takenValue).includes(field.name)
        );

        return {
            fields,
            usedMappings,
            unusedValues,
        };
    }

    private enabledSalesforceFieldsGenerator(
        usedSalesforceValues: { takenValue: string; takenIndex: number }[],
        unusedSalesforceValues: SalesforceFieldMetadata[],
        salesforceFields: SalesforceFieldMetadata[]
    ): { value: SalesforceFieldMetadata; reservedBy: number }[] {
        // Build claims purely from the provided arrays to avoid recursive signal reads
        const claims: ValueReservation[] = usedSalesforceValues
            .map((usedField, index) => {
                const matchIndex = salesforceFields.findIndex(f => f.name === usedField.takenValue);
                return matchIndex !== -1 ? { value: salesforceFields[matchIndex], reservedBy: index } : undefined;
            })
            .filter((fieldValue): fieldValue is ValueReservation => !!fieldValue);
        const unusedSalesforceValuesMapped: ValueReservation[] = unusedSalesforceValues.map(fieldValue => ({
            value: fieldValue,
            reservedBy: -1,
        }));

        const compare = (
            a: { value: SalesforceFieldMetadata; reservedBy: number },
            b: { value: SalesforceFieldMetadata; reservedBy: number }
        ) => {
            if (a.value.name < b.value.name) {
                return -1;
            }
            if (a.value.name > b.value.name) {
                return 1;
            }
            return 0;
        };

        return [...claims, ...unusedSalesforceValuesMapped]
            .filter((fieldValue): fieldValue is ValueReservation => !!fieldValue)
            .sort(compare);
    }
}
